import * as React from "react";
import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { ICustomBaseMap } from "../Map/Basemaps/BasemapWidget";
import { BaseMapsMode } from "../Map/enums";
import { useMapContext } from "./MapContext";

export interface IBaseMapContext {
    primaryMapId: string;
    setPrimaryMapId: Dispatch<SetStateAction<string>>;
    secondaryMapId: string;
    setSecondaryMapId: Dispatch<SetStateAction<string>>;
    widgetMode: BaseMapsMode;
    setWidgetMode: Dispatch<SetStateAction<BaseMapsMode>>;
    subBasemapConfig: any;
    setSubBasemapConfig: Dispatch<SetStateAction<any>>;
    clickedBasemap: ICustomBaseMap;
    setClickedBasemap: Dispatch<SetStateAction<ICustomBaseMap>>;
    onOverlayMapClosed: () => void;
    onOverlayMapSelected: (mapId: string) => void;
    isOverlayModeActive: boolean;
    setIsOverlayModeActive: Dispatch<SetStateAction<boolean>>;
    changeMap: (id: string) => void;
    secondMap: any;
    setSecondMap: Dispatch<SetStateAction<any>>;
    layerOpacity: number;
    setLayerOpacity: Dispatch<SetStateAction<number>>;
    height: number;
    setHeight: Dispatch<SetStateAction<number>>;
}

const appid = encodeURI(window.location.host + window.location.pathname);
const storageName = "GeoLiteBaseMap-" + appid;

const BaseMapContext = createContext<IBaseMapContext>(null);

export const useBaseMapContext = (): IBaseMapContext => useContext(BaseMapContext);

export const BaseMapContextProvider: FC = (props: React.PropsWithChildren<IBaseMapContext>) => {
    const { configA } = useMapContext();
    const [primaryMapId, setPrimaryMapId] = useState<string>(
        localStorage.getItem(storageName) || configA.basemaps[0].id
    );
    const [secondaryMapId, setSecondaryMapId] = useState<string>(null);
    const [widgetMode, setWidgetMode] = useState<BaseMapsMode>(BaseMapsMode.default);
    const [subBasemapConfig, setSubBasemapConfig] = useState<any>();
    const [clickedBasemap, setClickedBasemap] = useState<ICustomBaseMap>(
        configA.basemaps.find((basemap: ICustomBaseMap) => basemap.id === primaryMapId)
    );
    const [isOverlayModeActive, setIsOverlayModeActive] = useState<boolean>(false);
    const [secondMap, setSecondMap] = useState(null);
    const [layerOpacity, setLayerOpacity] = useState<number>(0.5);
    const [height, setHeight] = useState<number>(document.body.clientHeight / 2);

    useEffect(() => {
        primaryMapId && localStorage.setItem(storageName, primaryMapId);
    }, [primaryMapId]);

    useEffect(() => {
        secondMap && secondMap.setOpacity(layerOpacity);
    }, [secondMap, layerOpacity]);

    useEffect(() => {
        widgetMode !== BaseMapsMode.default && setIsOverlayModeActive(true);
    }, [widgetMode]);

    const prepareToSetSubBasemapConfig = (basemapConfig) =>
        setSubBasemapConfig(basemapConfig !== subBasemapConfig ? basemapConfig : null);

    const prepareToSetClickedBasemap = (basemap) => {
        setSubBasemapConfig(null);
        setClickedBasemap(basemap);
    };

    const onOverlayMapClosed = () => {
        setWidgetMode(BaseMapsMode.default);
        setIsOverlayModeActive(false);
        setSecondaryMapId(null);
    };

    const onOverlayMapSelected = (mapId: string) => {
        setSecondaryMapId(mapId);
        changeMap(primaryMapId);
        setSubBasemapConfig(null);
    };

    const changeMap = useCallback(
        (id: string) => {
            configA.basemaps.filter((basemap) => basemap.id === id)[0];
            setPrimaryMapId(id);
        },
        [widgetMode]
    );

    const values = {
        primaryMapId,
        setPrimaryMapId,
        secondaryMapId,
        setSecondaryMapId,
        widgetMode,
        setWidgetMode,
        subBasemapConfig,
        setSubBasemapConfig: prepareToSetSubBasemapConfig,
        clickedBasemap,
        setClickedBasemap: prepareToSetClickedBasemap,
        onOverlayMapClosed,
        onOverlayMapSelected,
        isOverlayModeActive,
        setIsOverlayModeActive,
        changeMap,
        secondMap,
        setSecondMap,
        layerOpacity,
        setLayerOpacity,
        height,
        setHeight
    };

    return <BaseMapContext.Provider value={values}>{props.children}</BaseMapContext.Provider>;
};
